import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Table,
    Input,
    Typography,
    Card,
    Space,
    Spin,
    App,
    Button
} from 'antd';
import {
    SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {authService, fiscalCashRegisterService} from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import debounce from 'lodash/debounce';

const { Text } = Typography;

// Функция для форматирования даты
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

const FiscalCashRegistersList = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const navigate = useNavigate();
    const { message } = App.useApp();

    const [fiscalCashRegisters, setFiscalCashRegisters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(false);

    const observer = useRef();
    const lastFiscalCashRegisterElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMoreFiscalCashRegisters();
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, error]);

    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Фискальные кассы');

        setRightContent(
            authService.hasPermission('pos:fiscal_cash_registers:create') && (<Button
                type="primary"
                onClick={() => navigate('/fiscal-cash-registers/create')}
            >
                Создать фискальную кассу
            </Button>)
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    // Загрузка записей при первой загрузке и при изменении поиска
    useEffect(() => {
        setError(false);
        fetchFiscalCashRegisters();
    }, [searchText]);

    // Функция для дебаунсинга поиска
    const debouncedSearch = useCallback(
        debounce((value) => {
            setSearchText(value);
        }, 500),
        []
    );

    // Обработчик изменения поля поиска
    const handleSearch = (e) => {
        debouncedSearch(e.target.value);
    };

    // Загрузка записей
    const fetchFiscalCashRegisters = async () => {
        try {
            setLoading(true);
            setFiscalCashRegisters([]);
            setNextCursor(null);
            setHasMore(true);

            const response = await fiscalCashRegisterService.getFiscalCashRegisters({
                search: searchText
            });

            if (response && response.status === 'success') {
                setFiscalCashRegisters(response.data.items);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить список фискальных касс');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке фискальных касс');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    // Загрузка дополнительных фискальных касс при прокрутке
    const loadMoreFiscalCashRegisters = async () => {
        if (!hasMore || loading || error) return;

        try {
            setLoading(true);

            const response = await fiscalCashRegisterService.getFiscalCashRegisters({
                cursor: nextCursor,
                search: searchText
            });

            if (response && response.status === 'success') {
                setFiscalCashRegisters(prev => [...prev, ...response.data.items]);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить больше фискальных касс');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке дополнительных фискальных касс');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            width: 350,
            render: (text, record) => (
                <Text strong>
                    {record.name}
                </Text>
            ),
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            width: 100,
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: 'Дата создания',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (text) => formatDate(text),
        },
    ];

    // Создаем данные для таблицы
    const tableData = fiscalCashRegisters.map((fiscalCashRegister, index) => ({
        ...fiscalCashRegister,
        key: fiscalCashRegister.id
    }));

    return (
        <>
            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Space>
                        <Input
                            placeholder="Поиск фискальных касс..."
                            prefix={<SearchOutlined />}
                            onChange={handleSearch}
                            style={{ width: 350 }}
                            allowClear
                        />
                    </Space>
                    <Typography.Text>
                        Всего: {total}
                    </Typography.Text>
                </div>

                {initialLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        <style>
                            {`
                                .table-horizontal-borders .ant-table-tbody > tr > td {
                                    border-left: none !important;
                                    border-right: none !important;
                                }
                                .table-horizontal-borders .ant-table-thead > tr > th {
                                    border-left: none !important;
                                    border-right: none !important;
                                }
                                .table-horizontal-borders .ant-table-tbody > tr:hover > td {
                                    background-color: #f5f5f5 !important;
                                }
                            `}
                        </style>
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            size="middle"
                            bordered={false}
                            className="table-horizontal-borders"
                            tableLayout="fixed"
                            scroll={{x: 1000}}
                            onRow={(record) => ({
                                onClick: () => navigate(`/fiscal-cash-registers/${record.id}`),
                                style: {cursor: 'pointer'}
                            })}
                        />
                        {loading && (
                            <div style={{textAlign: 'center', padding: '20px 0'}}>
                                <Spin/>
                            </div>
                        )}
                        {/* Элемент для отслеживания прокрутки */}
                        {hasMore && (
                            <div
                                ref={lastFiscalCashRegisterElementRef}
                                style={{height: 10, width: '100%'}}
                            />
                        )}
                        {!hasMore && fiscalCashRegisters.length > 0 && (
                            <div style={{textAlign: 'center', padding: '10px'}}>
                                <Typography.Text type="secondary">
                                    Больше записей нет
                                </Typography.Text>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </>
    );
};

export default FiscalCashRegistersList;